import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { PetsService } from '../../../../services/pets';

type VaccinationDateControl = 'vaccination_date' | 'next_due_date';

interface VaccinationCalendarState {
  show: boolean;
  viewDate: Date;
}

interface VaccinationCalendarDay {
  date: Date;
  dayNumber: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-petvaccinationssmobile',
  templateUrl: './petvaccinationssmobile.component.html',
  styleUrl: './petvaccinationssmobile.component.scss'
})
export class PetvaccinationssmobileComponent implements OnInit {

  allData: any[] = [];
  visibleData: any[] = [];

  contentReady: boolean = true;
  noData: boolean = false;

  vaccinationForm!: FormGroup;
  editMode: boolean = false;
  selectedVaccinationId: number | null = null;

  petId!: number;

  pageSize: number = 8;
  currentLimit: number = 8;
  isLoadingMore: boolean = false;
  hasMoreData: boolean = false;

  isAr: boolean = localStorage.getItem('language') === 'ar';

  calendarState: Record<VaccinationDateControl, VaccinationCalendarState> = {
    vaccination_date: {
      show: false,
      viewDate: new Date()
    },
    next_due_date: {
      show: false,
      viewDate: new Date()
    }
  };

  constructor(
    private petsService: PetsService,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initForm();

    this.route.queryParams.subscribe(params => {
      if (params['petId']) {
        this.petId = +params['petId'];
        this.loadVaccinations();
      } else {
        this.contentReady = false;
        this.noData = true;
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  initForm(): void {
    this.vaccinationForm = this.fb.group({
      vaccine_name: ['', Validators.required],
      manufacturer: [''],
      batch_number: [''],
      vaccination_date: [this.todayDate()],
      next_due_date: [''],
      authorized_vet: [''],
      notes: ['']
    });

    this.refreshCalendarViewsFromForm();
  }

  loadVaccinations(): void {
    if (!this.petId) return;

    this.contentReady = true;

    this.petsService.getVaccinationsByPetId(this.petId).subscribe({
      next: (data) => {
        this.allData = data || [];
        this.noData = this.allData.length === 0;

        this.currentLimit = this.pageSize;
        this.setVisibleData();

        this.contentReady = false;
      },
      error: (err) => {
        console.error('❌ Error loading vaccinations:', err);
        this.allData = [];
        this.visibleData = [];
        this.noData = true;
        this.contentReady = false;
      },
    });
  }

  setVisibleData(): void {
    this.visibleData = this.allData.slice(0, this.currentLimit);
    this.hasMoreData = this.visibleData.length < this.allData.length;
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMoreData || this.contentReady) return;

    this.isLoadingMore = true;

    setTimeout(() => {
      this.currentLimit += this.pageSize;
      this.setVisibleData();
      this.isLoadingMore = false;
    }, 250);
  }

  saveVaccination(): void {
    if (this.vaccinationForm.invalid) {
      this.vaccinationForm.markAllAsTouched();
      return;
    }

    const raw = this.vaccinationForm.getRawValue();

    const payload = {
      ...raw,
      vaccination_date: this.normalizeDateValue(raw.vaccination_date),
      next_due_date: raw.next_due_date ? this.normalizeDateValue(raw.next_due_date) : '',
      pet_id: this.petId
    };

    if (this.editMode && this.selectedVaccinationId) {
      this.petsService.updateVaccination(this.selectedVaccinationId, payload).subscribe({
        next: () => {
          Swal.fire('تم التحديث', 'تم تعديل بيانات التطعيم بنجاح', 'success');
          this.resetForm();
          this.loadVaccinations();
        },
        error: (err) => {
          console.error(err);
          Swal.fire('خطأ', 'حدث خطأ أثناء التعديل', 'error');
        },
      });
    } else {
      this.petsService.addVaccination(payload).subscribe({
        next: () => {
          Swal.fire('تم الحفظ', 'تمت إضافة التطعيم بنجاح', 'success');
          this.resetForm();
          this.loadVaccinations();
        },
        error: (err) => {
          console.error(err);
          Swal.fire('خطأ', 'تعذر إضافة التطعيم', 'error');
        },
      });
    }
  }

  editVaccination(vaccination: any): void {
    this.editMode = true;
    this.selectedVaccinationId = vaccination.id;

    this.vaccinationForm.patchValue({
      vaccine_name: vaccination.vaccine_name || '',
      manufacturer: vaccination.manufacturer || '',
      batch_number: vaccination.batch_number || '',
      vaccination_date: this.normalizeDateValue(vaccination.vaccination_date) || this.todayDate(),
      next_due_date: vaccination.next_due_date ? this.normalizeDateValue(vaccination.next_due_date) : '',
      authorized_vet: vaccination.authorized_vet || '',
      notes: vaccination.notes || ''
    });

    this.refreshCalendarViewsFromForm();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  deleteVaccination(id: number): void {
    Swal.fire({
      title: 'هل أنت متأكد؟',
      text: 'لن يمكنك التراجع بعد الحذف!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'نعم، احذف!',
      cancelButtonText: 'إلغاء'
    }).then((result) => {
      if (result.isConfirmed) {
        this.petsService.deleteVaccination(id).subscribe({
          next: () => {
            Swal.fire('تم الحذف!', 'تم حذف التطعيم بنجاح.', 'success');
            this.loadVaccinations();
          },
          error: (err) => {
            console.error(err);
            Swal.fire('خطأ', 'تعذر حذف التطعيم', 'error');
          },
        });
      }
    });
  }

  isUpcoming(dateString: string | Date): boolean {
    if (!dateString) return false;

    const nextDue = this.toSafeDate(dateString);
    if (!nextDue) return false;

    const today = new Date();
    const thirtyDaysFromNow = new Date();

    today.setHours(0, 0, 0, 0);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999);

    nextDue.setHours(0, 0, 0, 0);

    return nextDue >= today && nextDue <= thirtyDaysFromNow;
  }

  isOverdue(dateString: string | Date): boolean {
    if (!dateString) return false;

    const nextDue = this.toSafeDate(dateString);
    if (!nextDue) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    nextDue.setHours(0, 0, 0, 0);

    return nextDue < today;
  }

  resetForm(): void {
    this.editMode = false;
    this.selectedVaccinationId = null;

    this.vaccinationForm.reset({
      vaccine_name: '',
      manufacturer: '',
      batch_number: '',
      vaccination_date: this.todayDate(),
      next_due_date: '',
      authorized_vet: '',
      notes: ''
    });

    this.refreshCalendarViewsFromForm();
  }

  todayDate(): string {
    return this.formatDateOnly(new Date());
  }

  formatInputDate(date: string | Date): string {
    return this.normalizeDateValue(date);
  }

  trackByVaccinationId(index: number, item: any): number {
    return item?.id || index;
  }

  /* ================================
     Reusable Calendar
     نفس Calendar بتاع New Booking
     ================================ */

  toggleDateCalendar(controlName: VaccinationDateControl): void {
    const willOpen = !this.calendarState[controlName].show;

    this.closeAllDateCalendars(controlName);

    this.calendarState[controlName].show = willOpen;

    if (willOpen) {
      const selectedDate = this.parseDateOnly(this.vaccinationForm.get(controlName)?.value);
      const baseDate = selectedDate || new Date();

      this.calendarState[controlName].viewDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      );
    }
  }

  closeDateCalendar(controlName: VaccinationDateControl): void {
    this.calendarState[controlName].show = false;
  }

  closeAllDateCalendars(except?: VaccinationDateControl): void {
    (['vaccination_date', 'next_due_date'] as VaccinationDateControl[]).forEach((controlName) => {
      if (controlName !== except) {
        this.calendarState[controlName].show = false;
      }
    });
  }

  previousDateCalendarMonth(controlName: VaccinationDateControl): void {
    const currentViewDate = this.calendarState[controlName].viewDate;

    this.calendarState[controlName].viewDate = new Date(
      currentViewDate.getFullYear(),
      currentViewDate.getMonth() - 1,
      1
    );
  }

  nextDateCalendarMonth(controlName: VaccinationDateControl): void {
    const currentViewDate = this.calendarState[controlName].viewDate;

    this.calendarState[controlName].viewDate = new Date(
      currentViewDate.getFullYear(),
      currentViewDate.getMonth() + 1,
      1
    );
  }

  getDateCalendarTitle(controlName: VaccinationDateControl): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.calendarState[controlName].viewDate);
  }

  getDateCalendarWeekDays(): string[] {
    return this.isAr
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getDateCalendarDays(controlName: VaccinationDateControl): VaccinationCalendarDay[] {
    const viewDate = this.calendarState[controlName].viewDate;
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = this.parseDateOnly(this.vaccinationForm.get(controlName)?.value);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: selectedDate ? this.isSameDate(date, selectedDate) : false,

        // التطعيمات ممكن تكون تاريخ قديم، فمش بنقفل الأيام القديمة
        isDisabled: false
      };
    });
  }

  selectDateCalendarDate(controlName: VaccinationDateControl, date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    this.vaccinationForm.patchValue({
      [controlName]: this.formatDateOnly(selectedDate)
    });

    this.vaccinationForm.get(controlName)?.markAsTouched();
    this.vaccinationForm.get(controlName)?.updateValueAndValidity();

    this.closeDateCalendar(controlName);
  }

  getDateDisplay(controlName: VaccinationDateControl, placeholder: string): string {
    const selectedDate = this.parseDateOnly(this.vaccinationForm?.get(controlName)?.value);

    if (!selectedDate) {
      return placeholder;
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(selectedDate);
  }

  private refreshCalendarViewsFromForm(): void {
    (['vaccination_date', 'next_due_date'] as VaccinationDateControl[]).forEach((controlName) => {
      const selectedDate = this.parseDateOnly(this.vaccinationForm?.get(controlName)?.value);
      const baseDate = selectedDate || new Date();

      this.calendarState[controlName] = {
        show: false,
        viewDate: new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
      };
    });
  }

  private normalizeDateValue(value: any): string {
    if (!value) return '';

    const parsedDate = this.parseDateOnly(value);
    if (parsedDate) {
      return this.formatDateOnly(parsedDate);
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return this.formatDateOnly(date);
    }

    return String(value);
  }

  private parseDateOnly(value: any): Date | null {
    if (!value) return null;

    const cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim()
      .split(' ')[0];

    const match = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) return null;

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  private toSafeDate(value: any): Date | null {
    const dateOnly = this.parseDateOnly(value);
    if (dateOnly) return dateOnly;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private formatDateOnly(date: Date): string {
    const pad = (num: number) => String(num).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }
}